import Dialog from '@mui/material/Dialog';
import { Command } from 'cmdk';
import { atom, useAtom } from 'jotai';
import { useMemo } from 'react';

import { NewPackageFamilyForm } from '#src/routes/package-store/home/new-package-family-form.js';
import { useAbilityContext } from '#state/contexts/ability-context.js';
import { AddCommandBarProvider } from '../components/add-command-bar-provider.js';

import type { CommandBarProviderProps } from '../command-bar-types.js';

/**
 * Atom used to signal that the "New Package Family" dialog should open.
 * Lives outside the command bar so the dialog persists after the command bar closes.
 */
const createPackageFamilyDialogOpenAtom = atom(false);

const CREATE_PACKAGE_FAMILY_PROVIDER_ID = 'create-package-family';

function CreatePackageFamilyProvider({ onClose }: CommandBarProviderProps) {
	const [, setIsDialogOpen] = useAtom(createPackageFamilyDialogOpenAtom);

	return (
		<Command.Item
			keywords={['new', 'create', 'add', 'package', 'family', 'packageFamily']}
			onSelect={() => {
				setIsDialogOpen(true);
				onClose();
			}}
		>
			New Package Family
		</Command.Item>
	);
}

/**
 * Component that registers the "New Package Family" command in the command bar globally.
 * Only renders when the current user has `manage` permission on the `packageFamily` subject.
 *
 * The dialog is rendered at this level (outside the command bar) so it persists after
 * the command bar closes.
 */
export function RegisterCreatePackageFamilyProvider() {
	const ability = useAbilityContext();
	const [isDialogOpen, setIsDialogOpen] = useAtom(createPackageFamilyDialogOpenAtom);

	const provider = useMemo(
		() => ({
			id: CREATE_PACKAGE_FAMILY_PROVIDER_ID,
			component: CreatePackageFamilyProvider,
			props: {},
			group: 'Actions',
		}),
		[],
	);

	if (!ability.can('manage', 'packageFamily')) {
		return null;
	}

	return (
		<>
			<AddCommandBarProvider provider={provider} />
			<Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)}>
				<NewPackageFamilyForm onFinished={() => setIsDialogOpen(false)} />
			</Dialog>
		</>
	);
}
